import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import {
    ConfigurationOntologyForAutomaticProtocolPatchRequest,
    ConfigurationOntologyForAutomaticProtocolResponse
} from '@/modules/configurationModule/detail/automation/protocols/services/configuration-ontology-for-automatic-protocol/configuration-ontology-for-automatic-protocol.dto'
import {
    ConfigurationAutomationProtocolsModalBasicFormState,
    initialConfigurationAutomationProtocolsModalBasicFormState
} from '@/modules/configurationModule/detail/automation/protocols/components/modal-basic-form/configuration-automation-protocols-modal-basic-form.model'
import { configurationOntologyForAutomaticProtocolService } from '@/modules/configurationModule/detail/automation/protocols/services/configuration-ontology-for-automatic-protocol/configuration-ontology-for-automatic-protocol.service'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { CommonMostLikelyMorphologicCodeResponse } from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'

@Component
export default class ConfigurationAutomationProtocolsModalBasicForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly ontologyForAutomaticProtocol!: ConfigurationOntologyForAutomaticProtocolResponse

    @Emit()
    create(): Partial<ConfigurationOntologyForAutomaticProtocolResponse> {
        return {
            sampleType: this.state.ontologyForAutomaticProtocol.sampleType,
            topographicCode: this.state.ontologyForAutomaticProtocol.topographicCode,
            procedure: this.state.ontologyForAutomaticProtocol.procedure,
            mostLikelyMorphologicCode: this.state.ontologyForAutomaticProtocol.mostLikelyMorphologicCode
        }
    }

    @Emit()
    update(): void {}

    state: ConfigurationAutomationProtocolsModalBasicFormState
    constructor() {
        super()
        this.state = initialConfigurationAutomationProtocolsModalBasicFormState()
        this.state.ontologyForAutomaticProtocol = this.ontologyForAutomaticProtocol
    }

    get updateMode(): boolean {
        return !!this.ontologyForAutomaticProtocol?.id
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get isTopographicCodeDisabled(): boolean {
        return !this.state.ontologyForAutomaticProtocol.sampleType
    }

    get isProcedureDisabled(): boolean {
        return this.isTopographicCodeDisabled || !this.state.ontologyForAutomaticProtocol.topographicCode
    }

    get isMostLikelyMorphologicCodeDisabled(): boolean {
        return this.isProcedureDisabled || !this.state.ontologyForAutomaticProtocol.procedure
    }

    /// SERVICES CALLS ///

    async getSampleTypes(): Promise<CommonSampleTypeResponse[]> {
        const response = await this.$_toCallUnique(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getTopographicCodes(): Promise<CommonTopographicCodeResponse[]> {
        const response = await this.$_toCallUnique(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getProcedures(): Promise<CommonProcedureResponse[]> {
        const response = await this.$_toCallUnique(commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    async getMostLikelyMorphologicCodes(): Promise<CommonMostLikelyMorphologicCodeResponse[]> {
        const response = await this.$_toCallUnique(commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory({ laboratoryId: this.state.laboratoryId }))
        return response ?? []
    }

    private async _patchOntologiesForAutomaticProtocol(ontologyForAutomaticProtocolId: number, request: ConfigurationOntologyForAutomaticProtocolPatchRequest): Promise<ConfigurationOntologyForAutomaticProtocolResponse | null> {
        if (ontologyForAutomaticProtocolId && request) {
            const response = await this.$_toCallUnique(configurationOntologyForAutomaticProtocolService.patch(this.state.laboratoryId, ontologyForAutomaticProtocolId, request))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///

    private _patchOperation(): Promise<ConfigurationOntologyForAutomaticProtocolResponse | null> {
        return this._patchOntologiesForAutomaticProtocol(this.state.ontologyForAutomaticProtocol.id!, {
            sampleTypeId: this.state.ontologyForAutomaticProtocol.sampleType!.id,
            topographicCodeId: this.state.ontologyForAutomaticProtocol.topographicCode?.id ?? null,
            procedureId: this.state.ontologyForAutomaticProtocol.procedure?.id ?? null,
            mostLikelyMorphologicCodeId: this.state.ontologyForAutomaticProtocol.mostLikelyMorphologicCode?.id ?? null
        })
    }

    async doAction(): Promise<void> {
        const valid = await this.$validator.validateAll()
        if (valid) {
            if (this.updateMode) {
                if (await this._patchOperation()) {
                    this.update() // emits an event
                    await this.$validator.reset()
                }
            } else {
                this.create() // emits an event
                await this.$validator.reset()
            }
        }
    }
}
